"use client";

import dynamic from "next/dynamic";
import type { ComponentType } from "react";

import { ScaledSlide } from "@/components/MockupScene";
import type { HomeMarketingFeatureSlug } from "@/content/home-feature-modules";

type FeatureShowcaseVisualProps = {
  featureId: HomeMarketingFeatureSlug;
  className: string;
  /** Holds the demo on its current frame (the preview Reel keeps passed frames on screen). */
  isPaused?: boolean;
};

type VisualProps = { className: string; isPaused?: boolean };

const fillClass = "h-full min-h-0";

const WriteShowcaseVisual = dynamic(() =>
  import("../home-hero/script-editor-demo").then((module) => {
    const Visual = ({ className, isPaused = false }: VisualProps) => (
      <ScaledSlide size="fill" className={className}>
        <module.ScriptEditorDemoSlide isPaused={isPaused} density="showcase" />
      </ScaledSlide>
    );
    return { default: Visual };
  }),
);

const BreakdownShowcaseVisual = dynamic(() =>
  import("../home-hero/breakdown-demo").then((module) => {
    const Visual = ({ className, isPaused = false }: VisualProps) => (
      <ScaledSlide size="fill" className={className}>
        <module.BreakdownDemoSlide isPaused={isPaused} density="showcase" />
      </ScaledSlide>
    );
    return { default: Visual };
  }),
);

const CoverageShowcaseVisual = dynamic(() =>
  import("../home-hero/coverage-demo").then((module) => {
    const Visual = ({ className, isPaused = false }: VisualProps) => (
      <ScaledSlide size="fill" className={className}>
        <module.CoverageDemoSlide isPaused={isPaused} />
      </ScaledSlide>
    );
    return { default: Visual };
  }),
);

const ScheduleShowcaseVisual = dynamic(() =>
  import("../home-hero/schedule-demo").then((module) => {
    const Visual = ({ className, isPaused = false }: VisualProps) => (
      <ScaledSlide size="fill" className={className}>
        <module.ScheduleDemoSlide isPaused={isPaused} />
      </ScaledSlide>
    );
    return { default: Visual };
  }),
);

const BudgetShowcaseVisual = dynamic(() =>
  import("../home-hero/budget-demo").then((module) => {
    const Visual = ({ className, isPaused = false }: VisualProps) => (
      <ScaledSlide size="fill" className={className}>
        <module.BudgetDemoSlide isPaused={isPaused} />
      </ScaledSlide>
    );
    return { default: Visual };
  }),
);

const RbacShowcaseVisual = dynamic(() =>
  import("../feature-mockups/SecurityMockups").then((module) => {
    const Visual = ({ className }: VisualProps) => (
      <module.RbacMockup size="fill" className={className} />
    );
    return { default: Visual };
  }),
);

const showcaseVisuals: Record<
  HomeMarketingFeatureSlug,
  ComponentType<VisualProps>
> = {
  write: WriteShowcaseVisual,
  breakdown: BreakdownShowcaseVisual,
  coverage: CoverageShowcaseVisual,
  schedule: ScheduleShowcaseVisual,
  budget: BudgetShowcaseVisual,
  collaborate: RbacShowcaseVisual,
};

export const FeatureShowcaseVisual = ({
  featureId,
  className,
  isPaused = false,
}: FeatureShowcaseVisualProps) => {
  const Visual = showcaseVisuals[featureId];
  return <Visual className={className || fillClass} isPaused={isPaused} />;
};
